/**
 * batchOperations - Utilities for batching and optimizing offline queue operations
 * Groups similar operations for efficient replay
 */
import { OPERATION_TYPES } from './offlineQueueManager';

const MAX_BATCH_SIZE = 50; // Prevent request timeouts

/**
 * Group operations by type and table
 * @param {Array} operations - Array of queue operations
 * @returns {Object} Grouped operations
 */
export const groupOperationsByType = (operations) => {
  const groups = {
    claps: [],
    bookmarks: [],
    comments: [],
    storyUpdates: [],
  };

  operations?.forEach(op => {
    switch (op?.operationType) {
      case OPERATION_TYPES?.CLAP:
        groups?.claps?.push(op);
        break;
      case OPERATION_TYPES?.BOOKMARK:
        groups?.bookmarks?.push(op);
        break;
      case OPERATION_TYPES?.COMMENT:
        groups?.comments?.push(op);
        break;
      case OPERATION_TYPES?.STORY_UPDATE:
        groups?.storyUpdates?.push(op);
        break;
    }
  });

  return groups;
};

/**
 * Aggregate claps by story_id and sum counts
 * @param {Array} clapOps - Clap operations
 * @returns {Array} Aggregated clap operations
 */
export const aggregateClaps = (clapOps) => {
  const clapMap = new Map();

  clapOps?.forEach(op => {
    const storyId = op?.data?.story_id;
    const increment = op?.metadata?.clapIncrement || 1;

    if (clapMap?.has(storyId)) {
      const existing = clapMap?.get(storyId);
      existing.totalIncrement += increment;
      existing?.operations?.push(op);
    } else {
      clapMap?.set(storyId, {
        storyId,
        totalIncrement: increment,
        operations: [op],
        firstOp: op,
      });
    }
  });

  return Array.from(clapMap?.values());
};

/**
 * Deduplicate bookmarks by story_id, keeping latest state
 * @param {Array} bookmarkOps - Bookmark operations
 * @returns {Array} Deduplicated bookmark operations
 */
export const deduplicateBookmarks = (bookmarkOps) => {
  const bookmarkMap = new Map();

  bookmarkOps?.forEach(op => {
    const key = `${op?.data?.user_id}_${op?.data?.story_id}`;
    
    if (!bookmarkMap?.has(key) || op?.timestamp > bookmarkMap?.get(key)?.timestamp) {
      bookmarkMap?.set(key, op);
    }
  });

  return Array.from(bookmarkMap?.values());
};

/**
 * Group comments for bulk insert
 * @param {Array} commentOps - Comment operations
 * @returns {Array} Comment operations ready for batch insert
 */
export const groupComments = (commentOps) => {
  // Remove duplicates based on content and story_id
  const seen = new Set();
  const unique = [];

  commentOps?.forEach(op => {
    const key = `${op?.data?.story_id}_${op?.data?.author_id}_${op?.data?.content}`;
    if (!seen?.has(key)) {
      seen?.add(key);
      unique?.push(op);
    }
  });

  return unique;
};

/**
 * Keep only latest story update per story_id
 * @param {Array} storyOps - Story update operations
 * @returns {Array} Latest story updates
 */
export const deduplicateStoryUpdates = (storyOps) => {
  const storyMap = new Map();

  storyOps?.forEach(op => {
    const storyId = op?.data?.id;
    
    if (!storyMap?.has(storyId) || op?.timestamp > storyMap?.get(storyId)?.timestamp) {
      storyMap?.set(storyId, op);
    }
  });

  return Array.from(storyMap?.values());
};

/**
 * Create batches from operations with size limits
 * @param {Array} operations - Operations to batch
 * @param {number} maxSize - Maximum batch size
 * @returns {Array} Array of batches
 */
export const createBatches = (operations, maxSize = MAX_BATCH_SIZE) => {
  const batches = [];
  
  for (let i = 0; i < operations?.length; i += maxSize) {
    batches?.push(operations?.slice(i, i + maxSize));
  }
  
  return batches;
};

/**
 * Batch operations for optimized replay
 * @param {Array} operations - Queue operations
 * @returns {Object} Batched operations with metadata
 */
export const batchOperations = (operations) => {
  console.log(`[BatchOperations] Processing ${operations?.length} operations`);

  // Group by type
  const groups = groupOperationsByType(operations);

  // Aggregate claps
  const aggregatedClaps = aggregateClaps(groups?.claps);
  console.log(`[BatchOperations] Aggregated ${groups?.claps?.length} claps into ${aggregatedClaps?.length} operations`);

  // Deduplicate bookmarks
  const uniqueBookmarks = deduplicateBookmarks(groups?.bookmarks);
  console.log(`[BatchOperations] Deduplicated ${groups?.bookmarks?.length} bookmarks to ${uniqueBookmarks?.length}`);

  // Group comments
  const uniqueComments = groupComments(groups?.comments);
  console.log(`[BatchOperations] Grouped ${groups?.comments?.length} comments to ${uniqueComments?.length}`);

  // Deduplicate story updates
  const latestStoryUpdates = deduplicateStoryUpdates(groups?.storyUpdates);
  console.log(`[BatchOperations] Deduplicated ${groups?.storyUpdates?.length} story updates to ${latestStoryUpdates?.length}`);

  // Create batches with size limits
  const clapBatches = createBatches(aggregatedClaps);
  const bookmarkBatches = createBatches(uniqueBookmarks);
  const commentBatches = createBatches(uniqueComments);
  const storyUpdateBatches = createBatches(latestStoryUpdates);

  const totalBatches = clapBatches?.length + bookmarkBatches?.length + commentBatches?.length + storyUpdateBatches?.length;
  const totalOperations = aggregatedClaps?.length + uniqueBookmarks?.length + uniqueComments?.length + latestStoryUpdates?.length;

  console.log(`[BatchOperations] Created ${totalBatches} batches from ${totalOperations} operations`);

  return {
    claps: clapBatches,
    bookmarks: bookmarkBatches,
    comments: commentBatches,
    storyUpdates: storyUpdateBatches,
    stats: {
      originalCount: operations?.length,
      optimizedCount: totalOperations,
      batchCount: totalBatches,
      savedOperations: operations?.length - totalOperations,
    },
  };
};
